import { createUseStyles } from "react-jss";
import {
    foregroundColor,
    darken,
    alpha,
    getSpacing,
    controlBackgroundColor,
} from "../../theme/Mixins";
import { Theme } from "../../types/Theme";
import * as ChoiceTokens from "../../theme/choiceThemeTokens";

const isModernTheme = (theme: Theme) => theme.UseModernTheme === true;

const resolveModernCardFocusRingColor = (theme: Theme): string =>
    theme.NeutralDarkColor ?? theme.ForegroundColorDark;

/** Subtle inner-card outline for modern drag-drop ranking (lighter than ControlBorderColor). */
const resolveModernCardBorderColor = (theme: Theme): string =>
    alpha(theme.NeutralDarkColor ?? theme.ForegroundColorDark, 0.25);

const modernCardFocusPseudoStyles = {
    "&:focus-visible": {
        boxShadow: ({ theme }: { theme: Theme }) => {
            if (!isModernTheme(theme)) {
                return undefined;
            }
            const gap = theme.WhiteColor ?? theme.ControlBackground;
            const ring = resolveModernCardFocusRingColor(theme);
            return `0 0 0 2px ${gap}, 0 0 0 4px ${ring}`;
        },
    },
};

export default createUseStyles({
    container: {
        composes: "drag-drop",
        position: "relative",
        userSelect: "none",
        margin: ({ theme }) => (isModernTheme(theme) ? 0 : "auto"),
        maxWidth: 600,
        marginTop: ({ theme }) => getSpacing(theme, 0, theme.QuestionSpacing?.WithinComponents),
    },
    bucket: {
        composes: "drag-drop__list",
        border: "1px solid #999",
        position: "absolute",
        borderRadius: ({ theme }) => Math.min(theme.BorderRadius, 50),
    },
    bucketOrigin: {
        composes: "drag-drop__list--origin",
        border: ({ theme }) => [
            [Math.max(theme.BorderWidth, 1), "solid", theme.ControlBorderColor],
        ],
    },
    bucketOriginNoDrag: {
        composes: "drag-drop__list--origin--no-drag",
        cursor: "not-allowed",
    },
    bucketDestination: {
        composes: "drag-drop__list--destination",
        background: ({ theme }) =>
            `${alpha(theme.ControlBackground, theme.ControlAlpha)} 0% 0% no-repeat padding-box`,
        border: ({ theme }) => [[theme.BorderWidth, "solid", theme.ControlBorderColor]],
    },
    dropPlaceholder: {
        composes: "drag-drop__drop-placeholder",
        position: "absolute",
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        border: ({ theme }: { theme: Theme }) => [
            [Math.max(theme.BorderWidth, 2), "dashed", resolveModernCardBorderColor(theme)],
        ],
        boxSizing: "border-box",
        pointerEvents: "none",
        zIndex: 5,
    },
    card: {
        composes: "drag-drop__choice choice",
        background: ({ theme }: { theme: Theme }) =>
            alpha(theme.ControlBackground, theme.ControlAlpha),
        border: ({ theme }: { theme: Theme }) => [
            [
                theme.BorderWidth,
                "solid",
                isModernTheme(theme)
                    ? resolveModernCardBorderColor(theme)
                    : theme.ControlBorderColor,
            ],
        ],
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        color: ({ theme }: { theme: Theme }) =>
            foregroundColor(theme, controlBackgroundColor(theme)),
        position: "absolute",
        padding: 8,
        textAlign: "center",
        overflowWrap: "break-word",
        fontWeight: ({ theme }: { theme: Theme }) => (isModernTheme(theme) ? undefined : "bolder"),
        touchAction: "none",
        boxSizing: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? ("border-box" as const) : "border-box",
        backgroundSize: "cover",
        backgroundPosition: "center",
        cursor: "grab",
        outline: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? "2px solid transparent" : "none",
        transition: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? "background 200ms, border-color 200ms, color 200ms, box-shadow 200ms"
                : "background 200ms, border-color 200ms, color 200ms",

        "&:active": {
            cursor: "grabbing",
        },
    },
    cardModernFocus: {
        ...modernCardFocusPseudoStyles,
    },
    cardTextModern: {
        composes: "drag-drop__choice--text",
        height: "auto",
        minHeight: "unset",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: ({ theme }: { theme: Theme }) => ChoiceTokens.resolveChoicePaddingModern(theme),
    },
    cardSelected: {
        composes: "choice--selected",
        background: ({ theme }: { theme: Theme }) =>
            alpha(theme.ControlBackground, theme.ControlAlpha),
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? resolveModernCardBorderColor(theme) : theme.ControlBorderColor,
        boxShadow: "none",
        color: ({ theme }: { theme: Theme }) =>
            foregroundColor(theme, controlBackgroundColor(theme)),
    },
    cardHover: {
        composes: "choice--hover",
        background: ({ theme }: { theme: Theme }) => {
            if (!isModernTheme(theme)) {
                return undefined;
            }
            return alpha(theme.NeutralDarkColor, 0.08);
        },
    },
    cardDragging: {
        composes: "drag-drop__choice--dragging",
        background: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? theme.PrimaryColor : undefined,
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? theme.PrimaryColor : undefined,
        color: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? theme.WhiteColor : undefined,
    },
    cardActive: {
        composes: "choice--active",
        background: ({ theme }: { theme: Theme }) => {
            if (isModernTheme(theme)) {
                return undefined;
            }
            return darken(theme.ControlBackground, theme.ActiveBackgroundValue);
        },
        color: ({ theme }: { theme: Theme }) => {
            if (isModernTheme(theme)) {
                return undefined;
            }
            return foregroundColor(
                theme,
                darken(theme.ControlBackground, theme.ActiveBackgroundValue),
            );
        },
    },
    cardNoDrag: {
        composes: "drag-drop__choice--no-drag choice--disabled",
        pointerEvents: "none",
        cursor: "not-allowed",
        background: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? alpha(theme.ControlBackground, theme.ControlAlpha) : undefined,
        border: ({ theme }: { theme: Theme }) => [
            isModernTheme(theme)
                ? [theme.BorderWidth, "solid", alpha(theme.NeutralDarkColor, 0.35)]
                : [theme.BorderWidth, "solid", theme.ControlBorderColor],
        ],
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        color: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? alpha(theme.NeutralDarkColor, 0.35)
                : foregroundColor(theme, controlBackgroundColor(theme)),
        position: "absolute",
        padding: ({ theme, textOnly }: { theme: Theme; textOnly?: boolean }) =>
            isModernTheme(theme) && textOnly
                ? ChoiceTokens.resolveChoicePaddingModern(theme)
                : 8,
        textAlign: "center",
        fontWeight: ({ theme }: { theme: Theme }) => (isModernTheme(theme) ? undefined : "bolder"),
        boxSizing: "border-box",
        backgroundSize: "cover",
        backgroundPosition: "center",
        boxShadow: ({ theme }: { theme: Theme }) => (isModernTheme(theme) ? "none" : undefined),

        "&:active": {
            cursor: "grabbing",
        },
    },
});
